import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  ArrowRight, ArrowUpRight, Loader2, CheckCircle2, Users, Zap,
  ChevronLeft, RotateCcw, Star, Target, AlertTriangle, Shield,
} from 'lucide-react';

const VOTE_COLORS = { strongly_support: 'text-emerald-600 bg-emerald-500/10', support: 'text-emerald-500 bg-emerald-500/5', abstain: 'text-gray-500 bg-gray-500/10', oppose: 'text-red-500 bg-red-500/10' };

export function ExecutiveReport({ report, meeting, onBack }) {
  const [generating, setGenerating] = useState(false);
  const [boardGenerated, setBoardGenerated] = useState(meeting?.generated_board || null);
  const navigate = useNavigate();
  if (!report) return null;

  const advisorMap = {};
  (meeting?.advisors || []).forEach(a => { advisorMap[a.id] = a; });

  const handleGenerateBoard = async () => {
    if (!meeting?.meeting_id) return;
    setGenerating(true);
    try {
      const res = await apiPost(`/advisory/meetings/${meeting.meeting_id}/generate-board`, {});
      if (!res.ok) { toast.error('Failed to start'); setGenerating(false); return; }
      const { job_id } = await res.json();
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const pr = await apiGet(`/advisory/meeting-job/${job_id}`);
          if (pr.ok) {
            const job = await pr.json();
            if (job.status === 'complete') {
              toast.success(`Execution board created: ${job.lists_created} lists, ${job.cards_created} cards`);
              setBoardGenerated({ board_id: job.board_id });
              navigate(`/board/${job.board_id}`);
              setGenerating(false);
              return;
            }
            if (job.status === 'failed') { toast.error(job.error || 'Failed'); setGenerating(false); return; }
          }
        } catch { /* poll retry */ }
      }
      toast.error('Timed out');
    } catch { toast.error('Connection error'); }
    setGenerating(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="executive-report">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" /> Back to meetings</button>
      <div className="text-center"><h1 className="text-2xl font-bold mb-1">Executive Decision Report</h1><p className="text-sm text-muted-foreground">{meeting?.topic}</p></div>

      {report.executive_summary && (
        <section className="rounded-xl border-2 border-odapto-orange/30 bg-gradient-to-br from-odapto-orange/5 to-amber-500/5 p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-3"><Star className="w-5 h-5 text-odapto-orange" /> Executive Summary</h2>
          <p className="text-base leading-relaxed">{report.executive_summary}</p>
        </section>
      )}

      {report.consensus?.statement && (
        <section className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-3"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Board Consensus</h2>
          <p className="text-sm leading-relaxed mb-2">{report.consensus.statement}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Confidence:</span>
            <span className="text-sm font-bold text-emerald-600">{report.consensus.confidence}%</span>
            {report.consensus.supporting_advisors && <span className="text-xs text-muted-foreground ml-2">Supported by: {report.consensus.supporting_advisors.join(', ')}</span>}
          </div>
        </section>
      )}

      {report.key_recommendations?.length > 0 && (
        <section className="rounded-xl border border-blue-500/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><Target className="w-5 h-5 text-blue-500" /> Key Recommendations</h2>
          <div className="space-y-3">
            {report.key_recommendations.map((r, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background">
                <div className="flex items-start gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${r.priority === 'critical' ? 'bg-red-500/10 text-red-600' : r.priority === 'high' ? 'bg-amber-500/10 text-amber-600' : 'bg-blue-500/10 text-blue-600'}`}>{r.priority}</span>
                  <p className="text-sm font-medium flex-1">{r.recommendation}</p>
                </div>
                <p className="text-xs text-muted-foreground">{r.rationale}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                  {r.champion && <span>Champion: <strong>{r.champion}</strong></span>}
                  {r.timeline && <span>Timeline: <strong>{r.timeline.replace(/_/g, ' ')}</strong></span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {report.dissenting_views?.length > 0 && (
        <section className="rounded-xl border border-amber-500/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-500" /> Dissenting Views</h2>
          <div className="space-y-3">
            {report.dissenting_views.map((dv, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background">
                <p className="text-xs font-semibold text-amber-600 mb-1">{dv.advisor}</p>
                <p className="text-sm mb-1">{dv.position}</p>
                <p className="text-xs text-muted-foreground">{dv.reasoning}</p>
                {dv.risk_if_ignored && <p className="text-xs text-red-500 mt-1">Risk if ignored: {dv.risk_if_ignored}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {report.risk_assessment?.length > 0 && (
        <section className="rounded-xl border border-red-500/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><Shield className="w-5 h-5 text-red-500" /> Risk Assessment</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {report.risk_assessment.map((r, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background">
                <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${r.severity === 'high' ? 'bg-red-500/10 text-red-600' : r.severity === 'medium' ? 'bg-amber-500/10 text-amber-600' : 'bg-gray-500/10 text-gray-600'}`}>{r.severity}</span><p className="text-sm font-medium">{r.risk}</p></div>
                <p className="text-xs text-muted-foreground">{r.mitigation}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {report.board_vote?.length > 0 && (
        <section className="rounded-xl border border-violet-500/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-violet-500" /> Board Vote</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {report.board_vote.map((v, i) => {
              const adv = advisorMap[v.advisor_id] || {};
              const vc = VOTE_COLORS[v.vote] || VOTE_COLORS.abstain;
              return (
                <div key={i} className="p-3 rounded-lg border border-border bg-background flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: adv.avatar_color || '#666' }}>{(v.advisor_name || '?').split(' ').map(n => n[0]).join('')}</div>
                  <div className="flex-1 min-w-0"><p className="text-xs font-semibold">{v.advisor_name}</p><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${vc}`}>{v.vote?.replace(/_/g, ' ')}</span></div>
                  <span className="text-xs font-bold">{v.confidence}%</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {report.next_steps?.length > 0 && (
        <section className="rounded-xl border border-odapto-orange/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><Zap className="w-5 h-5 text-odapto-orange" /> Next Steps</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {report.next_steps.map((s, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background">
                <p className="text-sm font-medium mb-1 flex items-start gap-2"><ArrowUpRight className="w-3.5 h-3.5 text-odapto-orange shrink-0 mt-0.5" /> {s.action}</p>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  {s.owner && <span>Owner: <strong>{s.owner}</strong></span>}
                  {s.deadline && <span>By: <strong>{s.deadline}</strong></span>}
                </div>
                {s.success_metric && <p className="text-[10px] text-muted-foreground mt-0.5">Success: {s.success_metric}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="text-center pt-4 space-y-3">
        <div className="p-6 rounded-xl border-2 border-violet-500/30 bg-gradient-to-r from-violet-500/5 to-purple-500/5">
          <h3 className="font-semibold text-lg mb-2">{boardGenerated ? 'Execution Board Created' : 'Ready to Execute?'}</h3>
          <p className="text-sm text-muted-foreground mb-4">{boardGenerated ? 'Your board decision has been turned into actionable workstreams.' : 'Transform this board decision into a Kanban execution board with actionable cards.'}</p>
          {boardGenerated ? (
            <Button onClick={() => navigate(`/board/${boardGenerated.board_id}`)} className="bg-violet-600 hover:bg-violet-700 text-white px-6" data-testid="view-exec-board-btn"><ArrowRight className="w-4 h-4 mr-2" /> View Execution Board</Button>
          ) : (
            <Button onClick={handleGenerateBoard} disabled={generating} className="bg-violet-600 hover:bg-violet-700 text-white px-6" data-testid="generate-exec-board-btn">{generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Zap className="w-4 h-4 mr-2" /> Generate Execution Board</>}</Button>
          )}
        </div>
        <Button onClick={onBack} variant="outline"><RotateCcw className="w-4 h-4 mr-2" /> New Meeting</Button>
      </div>
    </div>
  );
}
